// Fase B - Dashboard rol Chef
//
// Listado de solicitudes de compra con acciones segun estado:
//   PENDIENTE      -> enviar-email | cancelar
//   ENVIADA_EMAIL  -> aprobar | cancelar
//   APROBADA       -> recibir | cancelar
//   RECIBIDA       -> (terminal)
//   CANCELADA      -> (terminal)
//
// Form para crear nueva solicitud (alimento + cantidad + motivo).
import { useEffect, useMemo, useState } from 'react'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { ModalShell, m, Pill } from '../components/ui/ModalShell'
import { useConfirm } from '../components/ConfirmDialog'
import {
  chefApi,
  type AlimentoLite, type SolicitudCompra,
  type PlatoEnCola, type BomResponse, type BomAgregadoItem,
  type InventarioItem, type LoteInventario, type AlertasStockResponse,
} from '../lib/api'

type Tab = 'solicitudes' | 'recetas' | 'mise-en-place' | 'inventario'

type EstadoFiltro = 'TODOS' | 'PENDIENTE' | 'ENVIADA_EMAIL' | 'APROBADA' | 'RECIBIDA' | 'CANCELADA'

const ESTADO_COLOR: Record<string, string> = {
  PENDIENTE: 'bg-yellow-100 text-yellow-900',
  ENVIADA_EMAIL: 'bg-blue-100 text-blue-900',
  APROBADA: 'bg-purple-100 text-purple-900',
  RECIBIDA: 'bg-green-100 text-green-900',
  CANCELADA: 'bg-gray-200 text-gray-700',
}

function formatFecha(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-SV', { dateStyle: 'short', timeStyle: 'short' })
}

export default function ChefDashboard() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const confirm = useConfirm()

  const [tab, setTab] = useState<Tab>('solicitudes')
  const [solicitudes, setSolicitudes] = useState<SolicitudCompra[]>([])
  const [alimentos, setAlimentos] = useState<AlimentoLite[]>([])
  const [filtro, setFiltro] = useState<EstadoFiltro>('TODOS')
  const [loading, setLoading] = useState(true)
  const [formAbierto, setFormAbierto] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [solRecibir, setSolRecibir] = useState<SolicitudCompra | null>(null)
  const [alertas, setAlertas] = useState<AlertasStockResponse | null>(null)
  const [alertasPanelOpen, setAlertasPanelOpen] = useState(false)

  const alimentoNombrePorId = useMemo(() => {
    const m = new Map<number, string>()
    for (const a of alimentos) m.set(a.id, a.nombre)
    return m
  }, [alimentos])

  async function refresh() {
    try {
      const [sols, als] = await Promise.all([
        chefApi.listarSolicitudes(filtro === 'TODOS' ? undefined : { estado: filtro }),
        alimentos.length === 0 ? chefApi.listarAlimentos() : Promise.resolve(alimentos),
      ])
      setSolicitudes(sols)
      if (alimentos.length === 0) setAlimentos(als as AlimentoLite[])
    } catch (err: any) {
      showToast(`Error al cargar: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const id = window.setInterval(refresh, 30_000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro])

  // Alertas de stock (badge cross-tab) - refresh cada 60s
  useEffect(() => {
    async function loadAlertas() {
      try {
        setAlertas(await chefApi.alertasStock())
      } catch {
        // silencioso: si falla, no rompemos el dashboard
      }
    }
    loadAlertas()
    const id = window.setInterval(loadAlertas, 60_000)
    return () => window.clearInterval(id)
  }, [])

  async function handleEnviarEmail(s: SolicitudCompra) {
    const ok = await confirm({
      title: 'Enviar email a compras',
      message: `Esto enviará un email al destinatario configurado para la solicitud #${s.id}. ¿Continuar?`,
      confirmText: 'Sí, enviar',
      variant: 'danger',
    })
    if (!ok) return
    setBusyId(s.id)
    try {
      await chefApi.enviarEmail(s.id)
      showToast(`Email enviado para solicitud #${s.id}`, 'success')
      await refresh()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  async function handleAprobar(s: SolicitudCompra) {
    // QA 5-jul: sin window.prompt (frágil en kiosco/PWA). El aprobador ES el
    // usuario logueado — más correcto y sin diálogo nativo.
    const aprobada_por = user?.nombre_completo || 'Chef'
    setBusyId(s.id)
    try {
      await chefApi.aprobar(s.id, aprobada_por)
      showToast(`Solicitud #${s.id} aprobada`, 'success')
      await refresh()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  function handleRecibir(s: SolicitudCompra) {
    setSolRecibir(s)
  }

  async function handleCancelar(s: SolicitudCompra) {
    const motivo = window.prompt(`Motivo para cancelar solicitud #${s.id}:`, '')
    if (!motivo || motivo.trim().length < 2) return
    setBusyId(s.id)
    try {
      await chefApi.cancelar(s.id, motivo.trim())
      showToast(`Solicitud #${s.id} cancelada`, 'success')
      await refresh()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  function accionesPara(s: SolicitudCompra) {
    const disabled = busyId === s.id
    switch (s.estado) {
      case 'PENDIENTE':
        // QA 5-jul: se agrega "Aprobar" en PENDIENTE (el backend ya lo permite
        // desde PENDIENTE o ENVIADA_EMAIL). Antes solo aparecía tras "Enviar
        // email", por lo que el ciclo de compras no se podía cerrar sin correo.
        return (
          <>
            <button onClick={() => handleEnviarEmail(s)} disabled={disabled}
              className="text-xs bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50">📧 Enviar email</button>
            <button onClick={() => handleAprobar(s)} disabled={disabled}
              className="text-xs bg-purple-600 text-white px-2 py-1 rounded disabled:opacity-50">✓ Aprobar</button>
            <button onClick={() => handleCancelar(s)} disabled={disabled}
              className="text-xs bg-gray-300 px-2 py-1 rounded disabled:opacity-50">Cancelar</button>
          </>
        )
      case 'ENVIADA_EMAIL':
        return (
          <>
            <button onClick={() => handleAprobar(s)} disabled={disabled}
              className="text-xs bg-purple-600 text-white px-2 py-1 rounded disabled:opacity-50">✓ Aprobar</button>
            <button onClick={() => handleCancelar(s)} disabled={disabled}
              className="text-xs bg-gray-300 px-2 py-1 rounded disabled:opacity-50">Cancelar</button>
          </>
        )
      case 'APROBADA':
        return (
          <>
            <button onClick={() => handleRecibir(s)} disabled={disabled}
              className="text-xs bg-green-600 text-white px-2 py-1 rounded disabled:opacity-50">📦 Recibir</button>
            <button onClick={() => handleCancelar(s)} disabled={disabled}
              className="text-xs bg-gray-300 px-2 py-1 rounded disabled:opacity-50">Cancelar</button>
          </>
        )
      default:
        return <span className="text-xs text-gray-400">terminal</span>
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader title="Chef — Solicitudes de compra" user={user} onLogout={logout}>
        {alertas && alertas.total_alertas > 0 && (
          <button
            type="button"
            onClick={() => setAlertasPanelOpen((v) => !v)}
            aria-label={`${alertas.total_alertas} alertas de inventario`}
            className="flex items-center gap-1 bg-avante-danger text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse hover:opacity-90"
          >
            ⚠ {alertas.total_alertas}
          </button>
        )}
      </AppHeader>

      {alertasPanelOpen && alertas && (
        <AlertasPanel alertas={alertas} onClose={() => setAlertasPanelOpen(false)} onGoInventario={() => { setTab('inventario'); setAlertasPanelOpen(false) }} />
      )}

      <main className="p-4 max-w-6xl mx-auto">
        <div className="flex gap-1 border-b mb-4">
          <button
            onClick={() => setTab('solicitudes')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'solicitudes' ? 'border-avante-primary text-avante-primary' : 'border-transparent text-gray-500'
            }`}
          >📋 Solicitudes compra</button>
          <button
            onClick={() => setTab('recetas')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'recetas' ? 'border-avante-primary text-avante-primary' : 'border-transparent text-gray-500'
            }`}
          >🍳 Recetas en cola</button>
          <button
            onClick={() => setTab('mise-en-place')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'mise-en-place' ? 'border-avante-primary text-avante-primary' : 'border-transparent text-gray-500'
            }`}
          >📊 Mise en place</button>
          <button
            onClick={() => setTab('inventario')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'inventario' ? 'border-avante-primary text-avante-primary' : 'border-transparent text-gray-500'
            }`}
          >📦 Inventario</button>
        </div>

        {tab === 'recetas' ? (
          <RecetasEnColaTab />
        ) : tab === 'mise-en-place' ? (
          <MiseEnPlaceTab />
        ) : tab === 'inventario' ? (
          <InventarioTab />
        ) : (
        <>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex gap-2 items-center">
            <label className="text-sm">Filtro estado:</label>
            <select value={filtro} onChange={(e) => setFiltro(e.target.value as EstadoFiltro)}
              className="border rounded px-2 py-1 text-sm">
              <option value="TODOS">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="ENVIADA_EMAIL">Enviada email</option>
              <option value="APROBADA">Aprobada</option>
              <option value="RECIBIDA">Recibida</option>
              <option value="CANCELADA">Cancelada</option>
            </select>
          </div>
          <button onClick={() => setFormAbierto(true)}
            className="bg-avante-primary text-white px-4 py-2 rounded text-sm font-semibold">
            + Nueva solicitud
          </button>
        </div>

        {loading && <p>Cargando…</p>}
        {!loading && solicitudes.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No hay solicitudes para este filtro.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {solicitudes.map((s) => (
            <article key={s.id} className="bg-white rounded-lg shadow p-3 border-l-4 border-avante-primary">
              <div className="flex justify-between items-start mb-1">
                <span className="font-semibold text-sm">#{s.id} · sede {s.sede_id}</span>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold ${ESTADO_COLOR[s.estado] ?? 'bg-gray-100'}`}>
                  {s.estado}
                </span>
              </div>
              <p className="text-sm font-bold">{alimentoNombrePorId.get(s.alimento_id) ?? `Alimento #${s.alimento_id}`}</p>
              <p className="text-xs text-gray-600">Cantidad: <strong>{s.cantidad_solicitada_g} g</strong></p>
              {s.motivo && <p className="text-xs text-gray-600 italic mt-1">"{s.motivo}"</p>}
              <p className="text-xs text-gray-400 mt-2">
                Solicitada: {formatFecha(s.solicitado_at)}
                {s.aprobada_at && <> · Aprobada: {formatFecha(s.aprobada_at)} ({s.aprobada_por})</>}
                {s.recibida_at && <> · Recibida: {formatFecha(s.recibida_at)}</>}
                {s.cancelada_at && <> · Cancelada: {formatFecha(s.cancelada_at)}</>}
              </p>
              {s.email_error && (
                <p className="text-xs text-red-600 mt-1">Email error: {s.email_error}</p>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">{accionesPara(s)}</div>
            </article>
          ))}
        </div>
        </>
        )}
      </main>

      {formAbierto && (
        <NuevaSolicitudModal
          alimentos={alimentos}
          onClose={() => setFormAbierto(false)}
          onCreated={() => { setFormAbierto(false); refresh() }}
        />
      )}

      {solRecibir && (
        <RecibirLoteModal
          solicitud={solRecibir}
          alimentoNombre={alimentoNombrePorId.get(solRecibir.alimento_id) ?? `Alimento #${solRecibir.alimento_id}`}
          onClose={() => setSolRecibir(null)}
          onRecibido={() => { setSolRecibir(null); refresh() }}
        />
      )}
    </div>
  )
}

function NuevaSolicitudModal({
  alimentos, onClose, onCreated,
}: { alimentos: AlimentoLite[]; onClose: () => void; onCreated: () => void }) {
  const { showToast } = useToast()
  const [alimentoId, setAlimentoId] = useState<number | ''>('')
  const [cantidad, setCantidad] = useState<string>('')
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    if (typeof alimentoId !== 'number' || alimentoId <= 0) {
      showToast('Seleccioná un alimento', 'error'); return
    }
    const cantidadNum = parseFloat(cantidad)
    if (isNaN(cantidadNum) || cantidadNum <= 0) {
      showToast('Cantidad debe ser numero > 0', 'error'); return
    }
    setSubmitting(true)
    try {
      await chefApi.crearSolicitud({
        alimento_id: alimentoId,
        cantidad_solicitada_g: cantidadNum,
        motivo: motivo.trim() || undefined,
      })
      showToast('Solicitud creada', 'success')
      onCreated()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell title="Nueva solicitud de compra" onClose={onClose} closeDisabled={submitting} maxWidth={480}>

        <label className="block text-sm font-semibold mb-1">Alimento:</label>
        <select value={alimentoId} onChange={(e) => setAlimentoId(parseInt(e.target.value) || '')}
          className="w-full p-2 border rounded text-sm mb-3">
          <option value="">— seleccionar —</option>
          {alimentos.map((a) => (
            <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>
          ))}
        </select>

        <label className="block text-sm font-semibold mb-1">Cantidad (g):</label>
        <input type="number" min="1" step="1" value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          className="w-full p-2 border rounded text-sm mb-3" placeholder="Ej: 1000" />

        <label className="block text-sm font-semibold mb-1">Motivo (opcional):</label>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
          rows={2} className="w-full p-2 border rounded text-sm mb-4"
          placeholder="Ej: stock bajo para menu de la semana" />

        <div className="flex gap-2">
          <button onClick={onClose} disabled={submitting}
            className="flex-1 border border-gray-300 py-2 rounded text-sm">Cancelar</button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 bg-avante-primary text-white py-2 rounded text-sm font-semibold disabled:opacity-50">
            {submitting ? 'Creando…' : 'Crear solicitud'}
          </button>
        </div>
    </ModalShell>
  )
}

// ============================================================
// Panel de alertas de stock (Fase C - PR #55)
// ============================================================
function AlertasPanel({
  alertas, onClose, onGoInventario,
}: {
  alertas: AlertasStockResponse
  onClose: () => void
  onGoInventario: () => void
}) {
  return (
    <div className="bg-red-50 border-y-2 border-red-300 px-4 py-3">
      <div className="max-w-6xl mx-auto flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-red-700 font-bold text-sm">⚠ Alertas de inventario</span>
            <span className="text-xs text-gray-600">
              {alertas.bajo_stock_count} bajo stock · {alertas.lotes_vencen_3d_count} vencen en 3d · {alertas.lotes_vencidos_count} vencidos
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {alertas.bajo_stock_preview.length > 0 && (
              <div>
                <p className="font-semibold mb-1 text-gray-700">Bajo stock (top 5):</p>
                <ul className="space-y-0.5">
                  {alertas.bajo_stock_preview.map((p) => (
                    <li key={p.alimento_codigo}>
                      <strong>{p.alimento_nombre}</strong>: {p.stock_actual_g.toFixed(0)} g
                      {p.punto_reorden_g != null && <span className="text-gray-500"> (reorden ≤ {p.punto_reorden_g.toFixed(0)})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {alertas.lotes_vencen_preview.length > 0 && (
              <div>
                <p className="font-semibold mb-1 text-gray-700">Vencen pronto (top 5):</p>
                <ul className="space-y-0.5">
                  {alertas.lotes_vencen_preview.map((l) => (
                    <li key={l.codigo_lote}>
                      <strong>{l.alimento_nombre}</strong>: lote {l.codigo_lote} vence {l.fecha_vencimiento} ({l.cantidad_actual_g.toFixed(0)} g)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <button onClick={onGoInventario}
            className="text-xs bg-avante-primary text-white px-3 py-1 rounded font-semibold">
            Ir a inventario →
          </button>
          <button onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700">Cerrar</button>
        </div>
      </div>
    </div>
  )
}


// ============================================================
// Modal recepcion de lote desde solicitud-compra APROBADA
// (Fase C - PR #54)
// ============================================================
function RecibirLoteModal({
  solicitud, alimentoNombre, onClose, onRecibido,
}: {
  solicitud: SolicitudCompra
  alimentoNombre: string
  onClose: () => void
  onRecibido: () => void
}) {
  const { showToast } = useToast()
  const hoy = new Date().toISOString().split('T')[0]
  const default30d = new Date(Date.now() + 30 * 86400_000).toISOString().split('T')[0]

  const [codigoLote, setCodigoLote] = useState(`L-${solicitud.id}-${Date.now().toString().slice(-6)}`)
  const [cantidad, setCantidad] = useState(solicitud.cantidad_solicitada_g)
  const [vencimiento, setVencimiento] = useState(default30d)
  const [proveedor, setProveedor] = useState('')
  const [factura, setFactura] = useState('')
  const [costo, setCosto] = useState('')
  const [notas, setNotas] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    if (!codigoLote.trim()) { showToast('Código de lote requerido', 'error'); return }
    const cantNum = parseFloat(String(cantidad))
    if (isNaN(cantNum) || cantNum <= 0) { showToast('Cantidad inválida', 'error'); return }
    if (!vencimiento) { showToast('Fecha de vencimiento requerida', 'error'); return }
    setSubmitting(true)
    try {
      await chefApi.recibir(solicitud.id, {
        codigo_lote: codigoLote.trim(),
        cantidad_recibida_g: cantNum,
        fecha_vencimiento: vencimiento,
        proveedor: proveedor.trim() || undefined,
        numero_factura: factura.trim() || undefined,
        costo_unitario: costo ? parseFloat(costo) : undefined,
        notas: notas.trim() || undefined,
      })
      showToast(`Lote ${codigoLote} recibido, stock actualizado`, 'success')
      onRecibido()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title="Recibir lote"
      subtitle={<>Solicitud <strong>#{solicitud.id}</strong> · {alimentoNombre} · solicitada: {solicitud.cantidad_solicitada_g} g</>}
      onClose={onClose}
    >

        <label className="block text-sm font-semibold mb-1">Código de lote *</label>
        <input value={codigoLote} onChange={(e) => setCodigoLote(e.target.value)}
          className="w-full p-2 border rounded text-sm mb-3 font-mono"
          placeholder="Ej: L-ABC-20260518" />

        <label className="block text-sm font-semibold mb-1">Cantidad recibida (g) *</label>
        <input type="number" min="1" step="any" value={String(cantidad)}
          onChange={(e) => setCantidad(e.target.value)}
          className="w-full p-2 border rounded text-sm mb-3" />

        <label className="block text-sm font-semibold mb-1">Fecha vencimiento *</label>
        <input type="date" value={vencimiento} min={hoy}
          onChange={(e) => setVencimiento(e.target.value)}
          className="w-full p-2 border rounded text-sm mb-3" />

        <label className="block text-sm font-semibold mb-1">Proveedor</label>
        <input value={proveedor} onChange={(e) => setProveedor(e.target.value)}
          className="w-full p-2 border rounded text-sm mb-3" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-semibold mb-1">N° factura</label>
            <input value={factura} onChange={(e) => setFactura(e.target.value)}
              className="w-full p-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Costo unit. ($)</label>
            <input type="number" min="0" step="0.01" value={costo}
              onChange={(e) => setCosto(e.target.value)}
              className="w-full p-2 border rounded text-sm" />
          </div>
        </div>

        <label className="block text-sm font-semibold mb-1">Notas</label>
        <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
          rows={2} className="w-full p-2 border rounded text-sm mb-4" />

        <p className="text-xs text-gray-500 mb-3">
          Esta acción creará un lote nuevo, sumará al inventario del alimento
          y registrará movimiento ENTRADA.
        </p>

        <div className="flex gap-2">
          <button onClick={onClose} disabled={submitting}
            className="flex-1 border border-gray-300 py-2 rounded text-sm">Cancelar</button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 bg-green-600 text-white py-2 rounded text-sm font-semibold disabled:opacity-50">
            {submitting ? 'Recibiendo…' : '📦 Confirmar recepción'}
          </button>
        </div>
    </ModalShell>
  )
}


// ============================================================
// Tab Recetas en cola: lista de platos a preparar + click muestra BOM
// ============================================================
function RecetasEnColaTab() {
  const { showToast } = useToast()
  const [platos, setPlatos] = useState<PlatoEnCola[]>([])
  const [loading, setLoading] = useState(true)
  const [selPlato, setSelPlato] = useState<PlatoEnCola | null>(null)

  async function refresh() {
    try {
      setPlatos(await chefApi.platosEnCola())
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const id = window.setInterval(refresh, 30_000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        Platos con porciones pendientes de preparar (estados OFERTA_CONFIRMADA + EN_PREPARACION).
        Click en uno para ver su receta (BOM).
      </p>

      {loading && <p>Cargando…</p>}
      {!loading && platos.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No hay platos en cola actualmente.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {platos.map((p) => (
          <button
            key={p.plato_id}
            onClick={() => setSelPlato(p)}
            className="bg-white rounded-lg shadow p-4 text-left hover:bg-blue-50 border-l-4 border-avante-accent transition"
          >
            <div className="flex justify-between items-start">
              <p className="font-bold text-sm">{p.plato_nombre}</p>
              <span className="bg-avante-warning text-black text-xs font-bold px-2 py-0.5 rounded">
                {p.porciones}×
              </span>
            </div>
            <p className="text-xs text-gray-500">cod: {p.plato_codigo}</p>
            <p className="text-xs text-blue-600 mt-2">Ver receta →</p>
          </button>
        ))}
      </div>

      {selPlato && (
        <BomModal plato={selPlato} onClose={() => setSelPlato(null)} />
      )}
    </>
  )
}

function BomModal({ plato, onClose }: { plato: PlatoEnCola; onClose: () => void }) {
  const { showToast } = useToast()
  const [bom, setBom] = useState<BomResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    chefApi.bomPlato(plato.plato_id)
      .then(setBom)
      .catch((err) => showToast(`Error BOM: ${err.response?.data?.detail || err.message}`, 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plato.plato_id])

  // Multiplica cantidades por las porciones para que el chef vea total a preparar
  const totalIngredientes = useMemo(() => {
    if (!bom) return []
    return bom.ingredientes.map((i) => ({
      ...i,
      cantidad_total: i.cantidad * plato.porciones,
      gramos_total: i.cantidad_en_gramos_calc != null
        ? i.cantidad_en_gramos_calc * plato.porciones
        : null,
    }))
  }, [bom, plato.porciones])

  return (
    <ModalShell
      title={plato.plato_nombre}
      subtitle={<span style={m.monoCyan}>cod: {plato.plato_codigo}</span>}
      onClose={onClose}
      maxWidth={640}
    >
      <div className="flex justify-end mb-2">
        <Pill tone="warn">{plato.porciones} porciones</Pill>
      </div>

        {loading && <p className="text-sm">Cargando receta…</p>}
        {!loading && totalIngredientes.length === 0 && (
          <p className="text-sm text-gray-500 italic py-4">
            Este plato no tiene BOM cargado todavía. Pedir a nutricionista que cargue ingredientes.
          </p>
        )}

        {totalIngredientes.length > 0 && (
          <table className="w-full text-sm mt-3">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-2">Ingrediente</th>
                <th className="text-right p-2">Por porción</th>
                <th className="text-right p-2">Total ({plato.porciones}×)</th>
                <th className="text-right p-2">Gramos total</th>
              </tr>
            </thead>
            <tbody>
              {totalIngredientes.map((i) => (
                <tr key={i.alimento_id} className="border-t">
                  <td className="p-2">
                    {i.alimento_nombre}
                    {i.es_principal && <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1 rounded">principal</span>}
                    {i.es_opcional && <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1 rounded">opcional</span>}
                    {i.observacion && <div className="text-xs text-gray-500 italic">{i.observacion}</div>}
                  </td>
                  <td className="p-2 text-right">{i.cantidad} {i.unidad}</td>
                  <td className="p-2 text-right font-bold">{i.cantidad_total.toFixed(2)} {i.unidad}</td>
                  <td className="p-2 text-right text-gray-600">
                    {i.gramos_total != null ? `${i.gramos_total.toFixed(0)} g` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-4 flex justify-end">
          <button onClick={onClose}
            className="border border-gray-300 px-4 py-2 rounded text-sm">Cerrar</button>
        </div>
    </ModalShell>
  )
}

// ============================================================
// Tab Mise en place: BOM agregado del dia
// (suma ingredientes de todas las comidas en cola para que el chef vea
// la lista total a preparar)
// ============================================================
function MiseEnPlaceTab() {
  const { showToast } = useToast()
  const [items, setItems] = useState<BomAgregadoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('')

  async function refresh() {
    try {
      setItems(await chefApi.bomAgregado())
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const id = window.setInterval(refresh, 30_000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibles = useMemo(() => {
    if (!filtro.trim()) return items
    const q = filtro.toLowerCase()
    return items.filter((i) =>
      i.alimento_nombre.toLowerCase().includes(q)
      || i.alimento_codigo.toLowerCase().includes(q),
    )
  }, [items, filtro])

  const totalKg = useMemo(() =>
    items.reduce((acc, i) => acc + (i.gramos_totales ?? 0), 0) / 1000,
    [items],
  )

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        Total de ingredientes a preparar (suma de TODAS las comidas en cola,
        excluyendo opcionales y excluidas por paciente). Útil para pedir al
        almacén o validar stock.
      </p>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Filtrar por nombre/código..."
          className="border rounded px-3 py-1 text-sm flex-1 min-w-[200px]"
        />
        <div className="text-sm text-gray-600">
          <strong>{items.length}</strong> ingredientes · <strong>{totalKg.toFixed(2)} kg</strong> total
        </div>
      </div>

      {loading && <p>Cargando…</p>}
      {!loading && items.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No hay comidas en cola que requieran preparación.
        </div>
      )}

      {visibles.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-2">Código</th>
                <th className="text-left p-2">Ingrediente</th>
                <th className="text-right p-2">Gramos totales</th>
                <th className="text-right p-2">Kg</th>
                <th className="text-right p-2">Porciones</th>
                <th className="text-right p-2">Platos</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((i) => (
                <tr key={i.alimento_id} className="border-t hover:bg-gray-50">
                  <td className="p-2 font-mono text-xs">{i.alimento_codigo}</td>
                  <td className="p-2">{i.alimento_nombre}</td>
                  <td className="p-2 text-right font-bold">
                    {i.gramos_totales != null ? i.gramos_totales.toFixed(0) : '—'}
                  </td>
                  <td className="p-2 text-right text-gray-600">
                    {i.gramos_totales != null ? (i.gramos_totales / 1000).toFixed(2) : '—'}
                  </td>
                  <td className="p-2 text-right">{i.porciones_que_lo_usan}</td>
                  <td className="p-2 text-right">{i.platos_distintos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ============================================================
// Tab Inventario: stock por sede + lotes FEFO
// ============================================================
function InventarioTab() {
  const { showToast } = useToast()
  const [items, setItems] = useState<InventarioItem[]>([])
  const [loading, setLoading] = useState(true)
  const [tipoFiltro, setTipoFiltro] = useState<'TODOS' | 'INGREDIENTE' | 'DESCARTABLE'>('TODOS')
  const [soloBajo, setSoloBajo] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [selAlimento, setSelAlimento] = useState<InventarioItem | null>(null)

  async function refresh() {
    setLoading(true)
    try {
      const params: { tipo?: string; q?: string; solo_bajo_stock?: boolean } = {}
      if (tipoFiltro !== 'TODOS') params.tipo = tipoFiltro
      if (soloBajo) params.solo_bajo_stock = true
      if (filtro.trim()) params.q = filtro.trim()
      setItems(await chefApi.listarInventario(params))
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoFiltro, soloBajo])

  const porCategoria = useMemo(() => {
    const m = new Map<string, InventarioItem[]>()
    for (const i of items) {
      const cat = i.categoria || '(sin categoría)'
      if (!m.has(cat)) m.set(cat, [])
      m.get(cat)!.push(i)
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [items])

  const stockBajoCount = useMemo(() => items.filter((i) => i.stock_bajo).length, [items])

  return (
    <>
      <p className="text-sm text-gray-600 mb-3">
        Stock disponible por alimento. Click en un item para ver sus lotes ordenados FEFO.
      </p>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value as any)}
          className="border rounded px-2 py-1 text-sm">
          <option value="TODOS">Todos los tipos</option>
          <option value="INGREDIENTE">Solo ingredientes</option>
          <option value="DESCARTABLE">Solo descartables</option>
        </select>
        <label className="text-sm flex items-center gap-1">
          <input type="checkbox" checked={soloBajo} onChange={(e) => setSoloBajo(e.target.checked)} />
          Solo stock bajo
        </label>
        <input value={filtro} onChange={(e) => setFiltro(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refresh()}
          placeholder="Buscar (Enter)..." className="border rounded px-3 py-1 text-sm flex-1 min-w-[180px]" />
        {stockBajoCount > 0 && (
          <span className="bg-avante-danger text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
            ⚠ {stockBajoCount} bajo stock
          </span>
        )}
      </div>

      {loading && <p>Cargando…</p>}
      {!loading && items.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No hay items de inventario con esos filtros.
        </div>
      )}

      {porCategoria.map(([cat, list]) => (
        <section key={cat} className="mb-5">
          <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase">{cat} <span className="text-gray-400 font-normal">({list.length})</span></h3>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-left p-2">Alimento</th>
                  <th className="text-right p-2">Stock g</th>
                  <th className="text-right p-2">Reorden</th>
                  <th className="text-right p-2">Mín / Máx</th>
                  <th className="text-left p-2">Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((i) => (
                  <tr key={i.id} className={`border-t ${i.stock_bajo ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                    <td className="p-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        i.tipo === 'DESCARTABLE' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                      }`}>{i.tipo}</span>
                    </td>
                    <td className="p-2">
                      <div>{i.alimento_nombre}</div>
                      <div className="text-xs text-gray-400 font-mono">{i.alimento_codigo}</div>
                    </td>
                    <td className={`p-2 text-right font-bold ${i.stock_bajo ? 'text-red-700' : ''}`}>
                      {i.stock_actual_g.toFixed(0)}
                    </td>
                    <td className="p-2 text-right text-gray-600">
                      {i.punto_reorden_g != null ? i.punto_reorden_g.toFixed(0) : '—'}
                    </td>
                    <td className="p-2 text-right text-xs text-gray-500">
                      {i.stock_minimo_g != null ? i.stock_minimo_g.toFixed(0) : '—'}
                      {' / '}
                      {i.stock_maximo_g != null ? i.stock_maximo_g.toFixed(0) : '—'}
                    </td>
                    <td className="p-2">
                      {i.stock_bajo
                        ? <span className="text-xs bg-avante-danger text-white px-2 py-0.5 rounded">⚠ Bajo</span>
                        : <span className="text-xs text-green-700">OK</span>}
                    </td>
                    <td className="p-2 text-right">
                      <button onClick={() => setSelAlimento(i)}
                        className="text-xs text-blue-600 hover:underline">Lotes →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {selAlimento && (
        <LotesModal item={selAlimento} onClose={() => setSelAlimento(null)} onChanged={refresh} />
      )}
    </>
  )
}

function LotesModal({ item, onClose, onChanged }: {
  item: InventarioItem
  onClose: () => void
  onChanged?: () => void
}) {
  const { showToast } = useToast()
  const [lotes, setLotes] = useState<LoteInventario[]>([])
  const [loading, setLoading] = useState(true)
  const [ajusteLote, setAjusteLote] = useState<LoteInventario | null>(null)
  const [conteoAbierto, setConteoAbierto] = useState(false)

  function cargarLotes() {
    setLoading(true)
    chefApi.lotesAlimento(item.alimento_id)
      .then(setLotes)
      .catch((err) => showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    cargarLotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.alimento_id])

  // QA 5-jul: el chip "Stock total" mostraba item.stock_actual_g (snapshot que
  // no refrescaba tras conteo/ajuste). Ahora se computa de los lotes cargados
  // (se refrescan solos), así siempre coincide con la tabla que se ve.
  const stockVivo = lotes.reduce((s, l) => s + l.cantidad_actual_g, 0)

  function diasParaVencer(fecha: string): number {
    const v = new Date(fecha).getTime()
    const hoy = Date.now()
    return Math.floor((v - hoy) / (1000 * 60 * 60 * 24))
  }

  return (
    <ModalShell
      title={item.alimento_nombre}
      subtitle={<>{item.categoria || '(sin categoría)'} · <span style={m.monoCyan}>cod: {item.alimento_codigo}</span></>}
      onClose={onClose}
      maxWidth={640}
    >
      <div className="flex justify-end mb-3">
        <Pill tone={item.stock_bajo ? 'danger' : 'ok'}>
          Stock total: {(loading ? item.stock_actual_g : stockVivo).toFixed(0)} g
        </Pill>
      </div>

      {loading && <p className="text-sm">Cargando lotes…</p>}
      {!loading && lotes.length === 0 && (
        <div className="rounded-xl p-4 mb-2 text-center" style={m.subCard}>
          <p className="text-sm italic mb-3" style={m.labelSuave}>
            Sin lotes activos para este alimento. Sin lote no hay FEFO ni ajuste:
            registra lo que hay físicamente en bodega.
          </p>
          <button
            onClick={() => setConteoAbierto(true)}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold"
            style={m.btnPrimary}
          >
            + Registrar conteo físico (crear lote)
          </button>
        </div>
      )}

      {lotes.length > 0 && (
        <div className="rounded-xl overflow-x-auto p-2" style={m.subCard}>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2" style={m.th}>Código lote</th>
                <th className="text-right p-2" style={m.th}>Actual g</th>
                <th className="text-right p-2" style={m.th}>Inicial g</th>
                <th className="text-left p-2" style={m.th}>Vence</th>
                <th className="text-left p-2" style={m.th}>Días</th>
                <th className="text-left p-2" style={m.th}>Estado</th>
                <th className="text-left p-2" style={m.th}>Proveedor</th>
                <th className="text-right p-2" style={m.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lotes.map((l) => {
                const dias = diasParaVencer(l.fecha_vencimiento)
                const vencido = dias < 0
                // Regla 6 Hospital-Tech: ámbar si vence en ≤7 días, verde si no
                const proximo = dias >= 0 && dias <= 7
                return (
                  <tr key={l.id}>
                    <td className="p-2 text-xs" style={{ ...m.monoCyan, ...m.tdBorde }}>{l.codigo_lote}</td>
                    <td className="p-2 text-right font-bold" style={{ ...m.mono, ...m.tdBorde }}>{l.cantidad_actual_g.toFixed(0)}</td>
                    <td className="p-2 text-right" style={{ ...m.mono, ...m.labelSuave, ...m.tdBorde }}>{l.cantidad_inicial_g.toFixed(0)}</td>
                    <td className="p-2 text-xs" style={{ ...m.mono, ...m.tdBorde }}>{l.fecha_vencimiento}</td>
                    <td className="p-2 text-xs font-bold" style={{
                      ...m.mono,
                      ...m.tdBorde,
                      color: vencido ? '#FF5C7A' : proximo ? '#F5A623' : '#2FE08A',
                    }}>
                      {vencido ? `vencido ${-dias}d` : `${dias}d`}
                    </td>
                    <td className="p-2" style={m.tdBorde}>
                      <Pill tone={
                        l.estado === 'DISPONIBLE' ? 'cyan'
                          : l.estado === 'VENCIDO' ? 'danger'
                          : l.estado === 'BLOQUEADO' ? 'warn'
                          : 'neutral'
                      }>{l.estado}</Pill>
                    </td>
                    <td className="p-2 text-xs" style={{ ...m.labelSuave, ...m.tdBorde }}>{l.proveedor || '—'}</td>
                    <td className="p-2 text-right" style={m.tdBorde}>
                      <button onClick={() => setAjusteLote(l)}
                        className="text-xs font-semibold hover:underline" style={{ color: '#7FD4FF' }}>Ajustar</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        {lotes.length > 0 ? (
          <button onClick={() => setConteoAbierto(true)}
            className="px-3 py-2 rounded-xl text-xs font-semibold" style={m.btnGhost}>
            + Lote por conteo físico
          </button>
        ) : <span />}
        <button onClick={onClose}
          className="px-4 py-2 rounded-xl text-sm font-semibold" style={m.btnPrimary}>Cerrar</button>
      </div>

      {conteoAbierto && (
        <ConteoFisicoModal
          item={item}
          onClose={() => setConteoAbierto(false)}
          onDone={() => {
            setConteoAbierto(false)
            cargarLotes()
            onChanged?.()
          }}
        />
      )}

      {ajusteLote && (
        <AjusteLoteModal
          lote={ajusteLote}
          onClose={() => setAjusteLote(null)}
          onDone={() => {
            setAjusteLote(null)
            cargarLotes()
            onChanged?.()
          }}
        />
      )}
    </ModalShell>
  )
}

const MOTIVOS_AJUSTE = [
  { valor: 'CONTEO', etiqueta: 'Conteo físico' },
  { valor: 'MERMA', etiqueta: 'Merma' },
  { valor: 'DANIO', etiqueta: 'Producto dañado' },
  { valor: 'VENCIDO', etiqueta: 'Vencido' },
  { valor: 'OTRO', etiqueta: 'Otro' },
] as const

function AjusteLoteModal({ lote, onClose, onDone }: {
  lote: LoteInventario
  onClose: () => void
  onDone: () => void
}) {
  const { showToast } = useToast()
  const [cantidad, setCantidad] = useState<string>('')
  const [motivo, setMotivo] = useState<string>('CONTEO')
  const [notas, setNotas] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const cantidadNum = cantidad === '' ? null : Number(cantidad)
  const diferencia = cantidadNum === null ? null : cantidadNum - lote.cantidad_actual_g
  const tipoPrevisto = diferencia === null || diferencia === 0
    ? null
    : diferencia > 0
      ? 'AJUSTE_POSITIVO'
      : ['MERMA', 'DANIO', 'VENCIDO'].includes(motivo)
        ? 'SALIDA_MERMA'
        : 'AJUSTE_NEGATIVO'

  async function guardar() {
    if (cantidadNum === null || diferencia === null || diferencia === 0) return
    setSubmitting(true)
    try {
      const r = await chefApi.ajustarLote(lote.id, {
        cantidad_real_g: cantidadNum,
        motivo,
        notas: notas.trim() || undefined,
      })
      showToast(
        `Lote ${r.codigo_lote}: ${r.cantidad_anterior_g.toFixed(0)} g → ${r.cantidad_real_g.toFixed(0)} g ` +
        `(${r.diferencia_g > 0 ? '+' : ''}${r.diferencia_g.toFixed(0)} g, ${r.tipo_movimiento})`,
        'success',
      )
      onDone()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title="Ajustar lote"
      subtitle={<><span style={m.monoCyan}>{lote.codigo_lote}</span> · sistema:{' '}
        <b>{lote.cantidad_actual_g.toFixed(0)} g</b> · vence {lote.fecha_vencimiento}</>}
      onClose={onClose}
      closeDisabled={submitting}
      maxWidth={480}
      zIndex={60}
    >

        <label className="block mb-3">
          <span className="text-sm font-semibold">Cantidad REAL contada (g) *</span>
          <input type="number" min="0" step="1" value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            disabled={submitting} autoFocus
            placeholder={`${lote.cantidad_actual_g.toFixed(0)}`}
            className="mt-1 w-full border rounded px-3 py-2 text-sm" />
        </label>

        <label className="block mb-3">
          <span className="text-sm font-semibold">Motivo *</span>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)}
            disabled={submitting}
            className="mt-1 w-full border rounded px-3 py-2 text-sm bg-white">
            {MOTIVOS_AJUSTE.map((m) => (
              <option key={m.valor} value={m.valor}>{m.etiqueta}</option>
            ))}
          </select>
        </label>

        <label className="block mb-3">
          <span className="text-sm font-semibold">Notas</span>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
            disabled={submitting} rows={2} maxLength={2000}
            placeholder="Detalle del ajuste (queda en bitácora)..."
            className="mt-1 w-full border rounded px-3 py-2 text-sm" />
        </label>

        {diferencia !== null && diferencia !== 0 && (
          <p className={`text-sm font-semibold mb-3 ${diferencia > 0 ? 'text-green-700' : 'text-red-700'}`}>
            Diferencia: {diferencia > 0 ? '+' : ''}{diferencia.toFixed(0)} g → {tipoPrevisto}
            {cantidadNum === 0 && ' (el lote quedará cerrado)'}
          </p>
        )}
        {diferencia === 0 && (
          <p className="text-sm text-gray-500 mb-3">Sin diferencia con el sistema — nada que ajustar.</p>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} disabled={submitting}
            className="flex-1 border py-2 rounded text-sm disabled:opacity-50">Cancelar</button>
          <button onClick={guardar}
            disabled={submitting || cantidadNum === null || diferencia === 0}
            className="flex-1 bg-avante-primary text-white py-2 rounded text-sm font-semibold disabled:opacity-50">
            {submitting ? 'Guardando…' : 'Guardar ajuste'}
          </button>
        </div>
    </ModalShell>
  )
}

function ConteoFisicoModal({ item, onClose, onDone }: {
  item: InventarioItem
  onClose: () => void
  onDone: () => void
}) {
  const { showToast } = useToast()
  const hoy = new Date()
  const defaultVence = new Date(hoy.getTime() + 30 * 86400000).toISOString().slice(0, 10)
  const [cantidad, setCantidad] = useState('')
  const [vencimiento, setVencimiento] = useState(defaultVence)
  const [notas, setNotas] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const cantidadNum = cantidad === '' ? null : Number(cantidad)

  async function guardar() {
    if (!cantidadNum || cantidadNum <= 0 || !vencimiento) return
    setSubmitting(true)
    try {
      const r = await chefApi.conteoFisico(item.alimento_id, {
        cantidad_g: cantidadNum,
        fecha_vencimiento: vencimiento,
        notas: notas.trim() || undefined,
      })
      showToast(
        `Lote ${r.codigo_lote} creado: ${r.cantidad_g.toFixed(0)} g · stock respaldado: ${r.stock_total_g.toFixed(0)} g`,
        'success',
      )
      onDone()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalShell
      title="Registrar conteo físico"
      subtitle={<>{item.alimento_nombre} · el stock queda respaldado por un lote real (FEFO + Ajustar)</>}
      onClose={onClose}
      closeDisabled={submitting}
      maxWidth={480}
      zIndex={60}
      footer={
        <div className="flex gap-2">
          <button onClick={onClose} disabled={submitting}
            className="flex-1 py-2 rounded-xl text-sm" style={m.btnGhost}>Cancelar</button>
          <button onClick={guardar}
            disabled={submitting || !cantidadNum || cantidadNum <= 0 || !vencimiento}
            className="flex-1 py-2 rounded-xl text-sm font-semibold disabled:opacity-50"
            style={m.btnPrimary}>
            {submitting ? 'Guardando…' : 'Crear lote'}
          </button>
        </div>
      }
    >
      <label className="block mb-3">
        <span className="text-sm font-semibold">Cantidad contada (g) *</span>
        <input type="number" min="1" step="1" value={cantidad} autoFocus
          onChange={(e) => setCantidad(e.target.value)} disabled={submitting}
          placeholder="Lo que pesaste/contaste en bodega"
          className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={m.input} />
      </label>
      <label className="block mb-3">
        <span className="text-sm font-semibold">Fecha de vencimiento *</span>
        <input type="date" value={vencimiento}
          onChange={(e) => setVencimiento(e.target.value)} disabled={submitting}
          className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={m.input} />
        <span className="text-xs" style={m.labelSuave}>El FEFO consume primero lo que vence primero.</span>
      </label>
      <label className="block">
        <span className="text-sm font-semibold">Notas</span>
        <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
          disabled={submitting} rows={2} maxLength={2000}
          placeholder="Ubicación, quién contó, observaciones…"
          className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={m.input} />
      </label>
    </ModalShell>
  )
}

